import { glideTopFloorPx } from '../../components/common/keyboardOverlayStyles';
import { keyboardKindFor, predictKeyboardTop } from '../../utils/ui/inlineRevealPlan';
import { findBodyScroller } from './modalHeightFreeze';
import { MIN_BODY_PX, CARD_BOTTOM_GAP_PX } from './modalBodyShrink';
import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { flipSlide, probePan } from './modalAnticipationSlide';
import { DISMISS_SUPPRESS_MS } from './modalCloseChoreography';

/**
 * TAP-REST (KAN-1181, founder decision 2026-10-31; replaces the open-time rest
 * latch of KAN-930). Every touch pop-up OPENS CENTRED. The moment the user TAPS
 * a typing field (the hook's WINDOW-capture focusin, before the inline guard and
 * before iOS samples the caret) the card GLIDES up to the header floor
 * (`glideTopFloorPx()`) as the keyboard rises, and for that typing session it is
 * a REST card: `data-cvx-kb-rest`, body shrink, no freeze / curtain / spacer.
 *
 * ONE mover: the layout moves to the floor SYNCHRONOUSLY (the scrim's padding is
 * written imperatively, like the 1b lift), then `flipSlide` paints the card
 * back at its old spot and glides it to the floor — its counter-transform is
 * only written in rAF (RD-DEV-020: iOS and the guard read rects incl.
 * transforms), so everything measured in the same handler (`shrinkFor`) sees the
 * POST-glide geometry. The same `anchor` the 1b lift uses carries the session:
 * its `releasing` glide is the keyboard close's way back to the CENTRE in the
 * same beat as the body release, and `returning` (KAN-561) glides back up.
 */

/** The tight floor never goes above this (px under the top edge / safe area). */
const TIGHT_FLOOR_MIN_PX = 8;

/** env(safe-area-inset-top) in px (a probe: CSS env() has no JS API). 0 where unsupported. */
export function safeAreaTopPx() {
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top, 0px)';
    document.body.appendChild(probe);
    const n = parseFloat(window.getComputedStyle(probe).paddingTop) || 0;
    document.body.removeChild(probe);
    return n;
  } catch { return 0; }
}

/**
 * The rest floor for a field tap. Normally the header floor; when the room
 * between it and the keyboard (kbTop − 8 − floor) cannot hold the card's chrome
 * (header + footer = dialog − body box) plus MIN_BODY_PX — a phone held
 * sideways — the TIGHT floor: the top safe area (min 8px). KAN-1181.
 */
export function restFloorPx(dialogEl, field) {
  const floor = glideTopFloorPx();
  const scroller = findBodyScroller(dialogEl, field);
  if (!scroller) return floor;
  const kbTop = predictKeyboardTop(window.innerHeight, keyboardKindFor(field));
  const chrome = dialogEl.offsetHeight - scroller.offsetHeight;
  if (kbTop - CARD_BOTTOM_GAP_PX - floor >= chrome + MIN_BODY_PX) return floor;
  return Math.min(floor, Math.max(safeAreaTopPx(), TIGHT_FLOOR_MIN_PX));
}

/**
 * @param {HTMLElement} dialogEl
 * @param {{ isEnabled: () => boolean, isMorphing: () => boolean, anchorRef: {current: object|null}, setAnchor: Function,
 *   keyboardSeenRef: {current: boolean}, keyboardOpenRef: {current: boolean},
 *   suppressUntilRef: {current: number}, onTapRest: (on: boolean) => void }} ctx
 */
export function createTapRest(dialogEl, {
  isEnabled, isMorphing, anchorRef, setAnchor, keyboardSeenRef, keyboardOpenRef, suppressUntilRef, onTapRest,
}) {
  let cancelFlip = null;
  let panTimer = null;
  const cancel = () => {
    if (cancelFlip) cancelFlip();
    cancelFlip = null;
    if (panTimer) clearTimeout(panTimer);
    panTimer = null;
  };

  /** Glide the card to the floor and make it a rest card. False = nothing to do. */
  function engage(field) {
    const scrimEl = dialogEl.parentElement;
    if (!scrimEl) return false;
    const floor = restFloorPx(dialogEl, field);
    // Client rect on purpose: a re-tap mid-glide measures where the card IS.
    const restingTop = dialogEl.getBoundingClientRect().top;
    // A card already at (or above) the floor stays where it is — still a rest card.
    const newTop = restingTop < floor ? Math.round(restingTop) : floor;
    // Imperative-first (synchronous, before iOS samples); `transition: none` also
    // cancels an in-flight release glide when the user re-taps mid-return.
    scrimEl.style.transition = 'none';
    scrimEl.style.alignItems = 'flex-start';
    scrimEl.style.paddingTop = `${newTop}px`;
    dialogEl.setAttribute('data-cvx-kb-rest', '');
    suppressUntilRef.current = Date.now() + DISMISS_SUPPRESS_MS;
    kbLog('tapRest', { from: Math.round(restingTop), to: newTop });
    cancel();
    cancelFlip = flipSlide(dialogEl, restingTop - newTop);
    panTimer = probePan('tap-rest');
    const existing = anchorRef.current;
    if (!existing) keyboardSeenRef.current = keyboardOpenRef.current;
    const next = {
      top: newTop,
      restingTop: existing?.restingTop ?? Math.round(restingTop),
      restingH: existing?.restingH ?? dialogEl.offsetHeight,
    };
    anchorRef.current = next;
    setAnchor(next);
    onTapRest(true);
    return true;
  }

  /**
   * The focusin gate. `rest` = the card already carries `data-cvx-kb-rest`: it
   * only re-engages when the keyboard-close glide to the centre is in flight
   * (a re-tap pulls it back up). Never mid enter-morph. ANY pop-up with a typing
   * field engages — also a tall card whose body already scrolls: the body then
   * clamps at MIN_BODY_PX and the guard reveals the field inside it (no clipped
   * footer, no spacer).
   */
  function maybeEngage(el, rest) {
    if (!isEnabled() || isMorphing()) return false;
    const a = anchorRef.current;
    if (rest && !(a && a.releasing != null)) return false;
    return engage(el);
  }

  return { maybeEngage, cancel };
}

export default createTapRest;
